import React, { useState, useContext } from 'react'
import { useParams, useNavigate } from "react-router-dom"
import { useQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import _ from 'lodash'
import { GET_TEMPLATE_VIEW_MODE } from './queries'
import { UserContext } from '../../context/userContext'
import { Link } from "react-router-dom"
import axios from "axios"
import { FirebaseContext } from "../../firebase"
import cloudFunctionsEndpoint from "../../config/cloudFunctionsEndpoint"
import { PrimeIcons } from 'primereact/api'
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import './style.css' // Asegúrate de crear este archivo CSS para los estilos

const StaffFormViewMode = () => {
    const userCtx = useContext(UserContext)
    const firebase = useContext(FirebaseContext)
    const navigate = useNavigate()
    const { userId } = useParams()
    const endpoint = cloudFunctionsEndpoint()

    const [parsedValues, setParsedValues] = useState(null)
    const [isLoadingPwdReset, setIsLoadingPwdReset] = useState(false)
    const [isLoadingDisable, setIsLoadingDisable] = useState(false)
    const [selectedUserPwdReset, setSelectedUserPwdReset] = useState(null)
    const [selectedUserDisable, setSelectedUserDisable] = useState(null)
    const [isAccountEnabled, setIsAccountEnabled] = useState(null)

    const formatDate = (dateString) => {
        return dateString ? DateTime.fromHTTP(dateString).toFormat('MMM d, yyyy') : ''
    }

    const { loading, error, data } = useQuery(GET_TEMPLATE_VIEW_MODE, {
        variables: {
            staff_id: userCtx.session.user.staff_id,
            organization_id: userCtx.session.user.organization_id,
            userId,
        },
        onCompleted: (data) => {
            const parsedData = data.getStaffProfileView[0]
            setParsedValues({
                ...parsedData,
                creationTime: formatDate(parsedData.creationTime),
                lastRefreshTime: formatDate(parsedData.lastRefreshTime),
                lastSignInTime: formatDate(parsedData.lastSignInTime),
            })
            setIsAccountEnabled(parsedData.disabled)
        }
    })

    const handlePasswordReset = async () => {
        const payload = {
            idTemplate: "password_reset",
            organization_alias: userCtx?.session?.user?.organization_alias,
            organization_id: userCtx?.session?.user?.organization_id,
            enterprise_id: userCtx?.session?.user?.enterprise_id,
            enterprise_alias: userCtx?.session?.user?.enterprise_alias,
            participant_alias: "not-set",
            participant_membership_id: "not-set",
            participant_id: "not-set",
            staff_alias: userCtx?.session?.user?.staff_alias,
            staff_id: userCtx?.session?.user?.staff_id,
            values: {
                staff_email_address: parsedValues.staff_email_address,
                staff_id: parsedValues.staff_id,
                signed_staff_name_first: parsedValues.staff_name_first,
                signed_staff_name_last: parsedValues.staff_name_last,
            },
        }
        setSelectedUserPwdReset(payload)
        setIsLoadingPwdReset(true)
        try {
            if (payload?.values?.staff_email_address) {
                let actionCodeSettings = {
                    // After password reset, the user will be give the ability to go back
                    // to this page.
                    url: window.location.origin,
                    handleCodeInApp: false,
                  }
                await firebase.sendPasswordResetEmail(_.trim(payload?.values?.staff_email_address), actionCodeSettings)
                await axios.post(`${endpoint}/sendPasswordResetEmail`, payload)
            }
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoadingPwdReset(false)
        }
    }

    const handleDisableAccount = async (isEnabled) => {
        const payload = {
            idTemplate: isEnabled ? "disable_account" : "enable_account",
            organization_alias: userCtx?.session?.user?.organization_alias,
            organization_id: userCtx?.session?.user?.organization_id,
            enterprise_id: userCtx?.session?.user?.enterprise_id,
            enterprise_alias: userCtx?.session?.user?.enterprise_alias,
            participant_alias: "not-set",
            participant_membership_id: "not-set",
            participant_id: "not-set",
            staff_alias: userCtx?.session?.user?.staff_alias,
            staff_id: userCtx?.session?.user?.staff_id,
            values: {
                staff_email_address: parsedValues.staff_email_address,
                staff_id: parsedValues.staff_id,
                isEnabled,
                signed_staff_name_first: parsedValues.staff_name_first,
                signed_staff_name_last: parsedValues.staff_name_last,
            },
        }
        setSelectedUserDisable(payload)
        setIsLoadingDisable(true)
        try {
            if (payload?.values?.staff_id) {
                await axios.post(`${endpoint}/disableAccount`, payload)
                setIsAccountEnabled(!isEnabled)
            }
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoadingDisable(false)
        }
    }

    if (loading) return <p>Loading...</p>
    if (error) return <p>Error: {error.message}</p>
    if (!parsedValues) return <p>No data available</p>

    return (
        <div className="main-content">
            <h1>Staff Account</h1>
            <div className="description-links-block">
                <div className="description-block">
                    <a href="#" onClick={handlePasswordReset} className={`link-button ${isLoadingPwdReset ? 'disabled-link' : 'reset-password-link'}`}>
                        {isLoadingPwdReset ? 'Resetting Password...' : 'Reset Password'}
                    </a>
                    <a 
                        href="#" 
                        onClick={() => handleDisableAccount(isAccountEnabled)} 
                        className={`link-button ${isLoadingDisable ? 'disabled-link' : isAccountEnabled ? 'enable-account-link' : 'disable-account-link'}`}
                    >
                        {isLoadingDisable ? (isAccountEnabled ? 'Enabling Account...' : 'Disabling Account...' ) : (isAccountEnabled ? 'Enable Account' : 'Disable Account' )}
                    </a>
                </div>
                <div className="links-block">
                    <Link className="btn-sm" to="/admin/users">
                        Close View
                    </Link>
                </div>
            </div>
            <p></p>
            <hr></hr>
            <div key={"staff"}>
                <div id="staff_id" className="view-data-field">
                    <div className="field-name"><strong>Staff ID</strong></div>
                    <div className="field-value"><div>{parsedValues?.staff_id}</div></div>
                </div>
                <div id="staff_name_first" className="view-data-field">
                    <div className="field-name"><strong>First Name</strong></div>
                    <div className="field-value"><div>{parsedValues?.staff_name_first}</div></div>
                </div>
                <div id="staff_name_last" className="view-data-field">
                    <div className="field-name"><strong>Last Name</strong></div>
                    <div className="field-value"><div>{parsedValues?.staff_name_last}</div></div>
                </div>
                <div id="staff_email_address" className="view-data-field">
                    <div className="field-name"><strong>Email Address</strong></div>
                    <div className="field-value">
                        <div>
                            {parsedValues?.staff_email_address} 
                            {parsedValues?.emailVerified ? (
                                <i className="pi pi-check-circle" style={{ color: 'green', marginLeft: '10px' }}></i>
                            ) : (
                                <i className="pi pi-times-circle" style={{ color: 'red', marginLeft: '10px' }}></i>
                            )}
                        </div>
                    </div>
                </div>
                <div id="staff_create_date" className="view-data-field">
                    <div className="field-name"><strong>Account creation date</strong></div>
                    <div className="field-value"><div>{parsedValues?.creationTime}</div></div>
                </div>
                <div id="staff_create_date" className="view-data-field">
                    <div className="field-name"><strong>Last active date</strong></div>
                    <div className="field-value"><div>{parsedValues?.lastRefreshTime}</div></div>
                </div>
                <div id="staff_create_date" className="view-data-field">
                    <div className="field-name"><strong>Last login date</strong></div>
                    <div className="field-value"><div>{parsedValues?.lastSignInTime}</div></div>
                </div>
            </div>
            <p></p>
            <hr></hr>
            <h2 className="profile-title">Staff Membership</h2>
            <div key={"memberships"}>
                <DataTable value={parsedValues?.staff_membership_ids}>
                    <Column field="organization_name" header="Organization" />
                    <Column field="role_name" header="Role" />
                    <Column field="staff_phone_number" header="Phone" body={(rowData) => rowData.staff_phone_number || 'N/A'} />
                    <Column field="staff_credentials" header="Credentials" body={(rowData) => rowData.staff_credentials || 'N/A'} />
                </DataTable>
            </div>
        </div>
    )
}

export default StaffFormViewMode